import { Link, useLocation } from "react-router-dom";
import { SidebarTrigger } from "@/components/ui/sidebar";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import { Bell, LogOut, Settings } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { UserAvatar } from "@/components/profile/UserAvatar";
import { NAV_BY_ROLE, SETTINGS_ITEM, type AppRole } from "@/lib/nav";
import NotificationBell from "@/components/notifications/NotificationBell";
import OrgSwitcher from "./OrgSwitcher";

interface Crumb {
  label: string;
  to?: string;
}

/**
 * Build breadcrumb trail: Home → Group → Page (if matched in NAV_BY_ROLE).
 * Falls back to a single page crumb for unknown routes (e.g. /settings).
 */
function buildBreadcrumb(role: AppRole, pathname: string): Crumb[] {
  const groups = NAV_BY_ROLE[role] ?? [];
  let bestGroup: string | null = null;
  let bestItem: { label: string; to: string } | null = null;
  for (const group of groups) {
    for (const item of group.items) {
      if (
        (pathname === item.to || pathname.startsWith(item.to + "/")) &&
        (!bestItem || item.to.length > bestItem.to.length)
      ) {
        bestItem = { label: item.label, to: item.to };
        bestGroup = group.label;
      }
    }
  }

  if (pathname === SETTINGS_ITEM.to) {
    return [{ label: SETTINGS_ITEM.label }];
  }
  if (!bestItem) return [{ label: "Dashboard" }];
  if (bestGroup) {
    return [{ label: bestGroup }, { label: bestItem.label }];
  }
  return [{ label: bestItem.label }];
}

export default function Topbar({ role }: { role: AppRole }) {
  const { user, signOut } = useAuth();
  const location = useLocation();
  const crumbs = buildBreadcrumb(role, location.pathname);

  return (
    <header className="sticky top-0 z-20 flex h-14 items-center gap-2 border-b bg-background/95 px-3 backdrop-blur supports-[backdrop-filter]:bg-background/80">
      <SidebarTrigger />
      <Breadcrumb className="hidden sm:flex">
        <BreadcrumbList>
          <BreadcrumbItem>
            <BreadcrumbLink asChild>
              <Link to="/">Home</Link>
            </BreadcrumbLink>
          </BreadcrumbItem>
          {crumbs.map((c, i) => (
            <span key={`${c.label}-${i}`} className="flex items-center gap-2">
              <BreadcrumbSeparator />
              <BreadcrumbItem>
                {c.to ? (
                  <BreadcrumbLink asChild>
                    <Link to={c.to}>{c.label}</Link>
                  </BreadcrumbLink>
                ) : i === crumbs.length - 1 ? (
                  <BreadcrumbPage>{c.label}</BreadcrumbPage>
                ) : (
                  <span className="text-muted-foreground">{c.label}</span>
                )}
              </BreadcrumbItem>
            </span>
          ))}
        </BreadcrumbList>
      </Breadcrumb>

      <div className="ml-auto flex items-center gap-2">
        {user && (role === "instructor" || role === "admin") && <OrgSwitcher />}
        {user && <NotificationBell role={role} />}

        {user && (
          <span className="hidden md:inline-block px-2 py-1 bg-primary/10 text-primary rounded-md text-xs">
            {user.email}
          </span>
        )}
        {user ? (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" className="h-8 w-8 p-0 rounded-full overflow-hidden">
                <span className="sr-only">Open user menu</span>
                <UserAvatar userId={user.id} size="sm" editable={false} fullSize={true} />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuItem asChild>
                <Link to="/settings" className="flex items-center">
                  <Settings className="mr-2 h-4 w-4" />
                  Settings
                </Link>
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => signOut()} className="flex items-center">
                <LogOut className="mr-2 h-4 w-4" />
                Sign out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        ) : (
          <div className="flex items-center gap-2">
            <span className="hidden sm:inline text-xs text-muted-foreground">
              Practicing as guest
            </span>
            <Button asChild size="sm" variant="outline">
              <Link to="/auth">Sign in</Link>
            </Button>
            <Button asChild size="sm">
              <Link to="/auth?mode=signup">Sign up free</Link>
            </Button>
          </div>
        )}
      </div>
    </header>
  );
}
